import type { PixelPoint } from '../../core/hex';

/** Smooth-pan duration per ADR-0014 §4. */
export const SMOOTH_PAN_DURATION_MS = 180;

/** ADR-0014 §3 easing palette — quad ease-out. */
export function easeOutQuad(t: number): number {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return 1 - (1 - clamped) * (1 - clamped);
}

export type CameraTweenMode = 'smooth' | 'snap';

/**
 * Pixel-space tween used by the renderer to smooth-pan the camera on movement
 * (ADR-0014 §4). Pure / Pixi-free so the camera follow behaviour can be
 * exercised in unit tests without booting a Pixi `Application`.
 *
 * Snap mode resets the tween to the new target immediately. Smooth mode
 * captures the current visual position as the `from` and interpolates over
 * `SMOOTH_PAN_DURATION_MS` with ease-out quad. Re-targeting mid-tween is safe
 * — `from` becomes the current value, so the camera lerps smoothly even when
 * the player moves before the previous pan finishes.
 */
export class CameraTween {
  private from: PixelPoint;
  private to: PixelPoint;
  private value: PixelPoint;
  private elapsedMs = 0;
  private durationMs = 0;

  constructor(initial: PixelPoint) {
    this.from = initial;
    this.to = initial;
    this.value = initial;
  }

  current(): PixelPoint {
    return this.value;
  }

  isAnimating(): boolean {
    return this.durationMs > 0;
  }

  setTarget(target: PixelPoint, mode: CameraTweenMode = 'snap'): void {
    if (mode === 'snap') {
      this.from = target;
      this.to = target;
      this.value = target;
      this.elapsedMs = 0;
      this.durationMs = 0;
      return;
    }
    this.from = this.value;
    this.to = target;
    this.elapsedMs = 0;
    this.durationMs = SMOOTH_PAN_DURATION_MS;
  }

  /** Advance the tween. Returns `true` if the visual value moved this tick. */
  tick(deltaMs: number): boolean {
    if (this.durationMs === 0) return false;
    this.elapsedMs += deltaMs;
    if (this.elapsedMs >= this.durationMs) {
      this.value = this.to;
      this.elapsedMs = 0;
      this.durationMs = 0;
      return true;
    }
    const t = easeOutQuad(this.elapsedMs / this.durationMs);
    this.value = {
      x: this.from.x + (this.to.x - this.from.x) * t,
      y: this.from.y + (this.to.y - this.from.y) * t,
    };
    return true;
  }
}
